import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import type { UnifiedIconName } from '@/components/UnifiedIcon';
import { UnifiedIcon } from '@/components/UnifiedIcon';

export interface SidebarNavItemProps {
  label: string;
  icon: UnifiedIconName;
  active?: boolean;
  onClick?: () => void;
  to?: string;
  end?: boolean;
  'data-testid'?: string;
}

/**
 * Sidebar nav block with success fill when active (reference: Overview).
 */
export function SidebarNavItem({
  label,
  icon,
  active = false,
  onClick,
  to,
  end = false,
  'data-testid': dataTestId,
}: SidebarNavItemProps) {
  const className = [
    'flex w-full items-center gap-3 rounded-[var(--kc-radius-sm)] px-3 py-2.5 text-sm font-medium transition-colors',
    active ? 'ds-nav-sidebar-active' : 'ds-nav-sidebar-idle',
  ].join(' ');

  if (to) {
    return (
      <NavLink
        to={to}
        end={end}
        data-testid={dataTestId ?? 'design-system-sidebar-nav-item'}
        className={({ isActive }) =>
          [
            'flex w-full items-center gap-3 rounded-[var(--kc-radius-sm)] px-3 py-2.5 text-sm font-medium transition-colors',
            isActive ? 'ds-nav-sidebar-active' : 'ds-nav-sidebar-idle',
          ].join(' ')
        }
        onClick={onClick}
      >
        <UnifiedIcon name={icon} size={20} className="h-5 w-5 shrink-0" />
        {label}
      </NavLink>
    );
  }

  return (
    <button
      type="button"
      data-testid={dataTestId ?? 'design-system-sidebar-nav-item'}
      className={className}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
    >
      <UnifiedIcon name={icon} size={20} className="h-5 w-5 shrink-0" />
      {label}
    </button>
  );
}

export interface TopNavTab {
  id: string;
  label: string;
  path?: string;
  end?: boolean;
  active?: boolean;
  activePrefixes?: string[];
}

export interface TopNavTabsProps {
  tabs: TopNavTab[];
  onTabClick?: (id: string) => void;
  trailing?: ReactNode;
  variant?: 'default' | 'inline';
  'data-testid'?: string;
}

/**
 * Horizontal top navigation tabs (Dashboard, Agents, Settings).
 */
export function TopNavTabs({
  tabs,
  onTabClick,
  trailing,
  variant = 'default',
  'data-testid': dataTestId,
}: TopNavTabsProps) {
  const { pathname } = useLocation();

  const isTabActive = (tab: TopNavTab, navLinkActive: boolean) => {
    if (tab.activePrefixes?.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
      return true;
    }

    return navLinkActive;
  };

  const containerClass =
    variant === 'inline'
      ? 'flex items-center justify-between gap-4'
      : 'flex items-center justify-between gap-4 border-b border-default pb-0';

  return (
    <div data-testid={dataTestId ?? 'design-system-top-nav-tabs'} className={containerClass}>
      <div className="flex items-center gap-6 overflow-x-auto">
        {tabs.map((tab) => {
          const tabClass = (isActive: boolean) =>
            `whitespace-nowrap pb-3 text-sm font-medium ${isActive ? 'ds-nav-tab-active' : 'ds-nav-tab-idle'}`;

          if (tab.path) {
            return (
              <NavLink
                key={tab.id}
                to={tab.path}
                end={tab.end}
                data-testid={`${dataTestId ?? 'design-system-top-nav-tabs'}-${tab.id}`}
                className={({ isActive }) => tabClass(isTabActive(tab, isActive))}
              >
                {tab.label}
              </NavLink>
            );
          }

          return (
            <button
              key={tab.id}
              type="button"
              data-testid={`${dataTestId ?? 'design-system-top-nav-tabs'}-${tab.id}`}
              onClick={() => onTabClick?.(tab.id)}
              className={tabClass(Boolean(tab.active))}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {trailing ? <div className="flex shrink-0 items-center gap-2 pb-2">{trailing}</div> : null}
    </div>
  );
}
